<main class="page-main">
	<app-page-sub-nav [data]="pageSubNavData" [currVisible]="currVisible"></app-page-sub-nav>

	<section class="page-section" id="overview">
		<h2 id="sectionTitleOverview" class="page-section-title" title="Copy Link to Clipboard"
			(click)="copyToClipboard('overview')">
			Overview <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
		</h2>
		<p class="page-section-content">
			Icons are small symbols that represent actions and items. Icons help
			users complete tasks by giving them visual cues. Icons are different
			from illustrations, which are larger and more detailed.
		</p>
	</section>
	<section class="page-section" id="icon-library">
		<h2 id="sectionTitlePrinciples" class="page-section-title" title="Copy Link to Clipboard"
			(click)="copyToClipboard('icon-library')">
			Icon library <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
		</h2>
		<div class="shield-search">
			<div class="search-input-container">
				<kendo-svg-icon [icon]="filterIcon" class="search-icon"></kendo-svg-icon>
				<input #SearchInput 
					class="search-input" 
					autocomplete="off" 
					name="search-input" 
					value="{{ inputValue }}"
					(keyup)="onSearchInput()" 
					type="text" 
					id="icon-search" 
					aria-activedescendant="1" 
					aria-autocomplete="list" 
					aria-owns="results"
					aria-required="false"
					data-active-option="1"
					placeholder="Filter Icon Library" />

					<!--
					role="combobox" - A11Y removed, a semanitcally correct conmbo needs an child element. the functionality here doens't need that.
					
					aria-expanded="inputDropDownShown" - Deque seems to be finding a false postive inputDropDownShown is just a boolean which is the correct type of value for aria-expanded
					-->
				<kendo-svg-icon [icon]="xIcon" class="x-icon" [hidden]="hideClearIcon"
					(click)="clearIconFilter()"></kendo-svg-icon>
			</div>
		</div>
		<ul class="page-icon-library-list">
			<li *ngFor="let icon of filteredData" class="page-icon-library-list-item">
				<div class="icon-container" (click)="openIconDetail(icon)">
					<kendo-svg-icon [icon]="iconLookup(icon)" [class]="'icon-content custom48'"></kendo-svg-icon>
					<p class="icon-label">
						{{ icon.name }}
					</p>
				</div>
			</li>
		</ul>
		<p *ngIf="!filteredData.length">No icons found. Try another keyword.</p>
		<app-icons-detail *ngIf="showIconDetail" [iconData]="detailData"
			(closeEvent)="closeIconDetail($event)"></app-icons-detail>
	</section>
	<section class="page-section" id="principles">
		<h2 id="sectionTitlePrinciples" class="page-section-title" title="Copy Link to Clipboard"
			(click)="copyToClipboard('principles')">
			Principles <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
		</h2>
		<ul class="page-section-list">
			<li class="page-section-list-item">
				<span class="page-section-list-item-sub-title">Less is
					more.</span>
				Do use icons meaningfully, but don’t rely on icons to
				communicate or use them for decoration. When there are too many
				icons on a UI it can become cluttered. Try using a single icon
				to capture a group of items, instead of a different icon for
				each item on a list.
			</li>
			<li class="page-section-list-item">
				When designing new icons, do aim for simplicity, don’t be over
				detailed.
			</li>
			<li class="page-section-list-item">
				<span class="page-section-list-item-sub-title">Be literal, not
					abstract.</span>
				Icons work best when they mirror real world objects. That’s why
				magnifying glasses are recognizable as search icons, or why hour
				glasses signal waiting. If someone needs to think about what an
				icon is or why it’s there, it isn’t the right icon.
			</li>
			<li class="page-section-list-item">
				<span class="page-section-list-item-sub-title">Consistency is
					key.</span>
				Icons are effective when they reduce cognitive load &#8212; the
				effort it takes to process and retain information. Using
				different icons to symbolize the same thing across multiple
				products is confusing and adds to the load. Inconsistency forces
				our users to understand and remember an icon’s meaning and
				purpose.
			</li>
			<li class="page-section-list-item">
				Do use the same icons for every product and every interface.
				Don’t use too many variations of the same icon.
			</li>
			<li class="page-section-list-item">
				Do use standard and instantly recognizable icons, like a gear
				for settings. This is an example of a universal icon that always
				means the same thing, or a house to navigate home. You don’t
				need to design new icons where an existing one will work just
				fine.
			</li>
			<li class="page-section-list-item">
				Directional icons are also universal, like an x for Close or
				left-pointing arrow for Back.
			</li>
		</ul>
	</section>
	<section class="page-section" id="icons-text">
		<h2 id="sectionTitleIconText" class="page-section-title" title="Copy Link to Clipboard"
			(click)="copyToClipboard('icon-text')">
			Icons and text <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
		</h2>
		<ul class="page-section-list">
			<li class="page-section-list-item">
				Icons should nearly always be accompanied by a title or label so
				their meaning can be understood by everyone. This is the easiest
				way to ensure icons are accessible.
			</li>
			<li class="page-section-list-item">
				Do always try to pair an icon with text. Don’t replace text with
				an icon.
			</li>
			<li class="page-section-list-item">
				Best practices and examples for writing the text to accompany
				the icon is on each component page, and
				<a href="guidelines/content">the content guidelines</a>.
			</li>
		</ul>
	</section>
	<section class="page-section" id="icon-anatomy">
		<h2 id="sectionTitleIconAnatomy" class="page-section-title" title="Copy Link to Clipboard"
			(click)="copyToClipboard('icon-anatomy')">
			Icon anatomy <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
		</h2>
		<ol class="page-section-list" style="margin-left: -20px">
			<li class="page-section-list-item" style="list-style: auto">
				Grid area: The boundaries that an icon can be placed within.
				Regular icons are designed on a 30px grid and exported on a 32px
				canvas.
			</li>
			<li class="page-section-list-item" style="list-style: auto">
				Icon.
			</li>
			<li class="page-section-list-item" style="list-style: auto">
				Canvas area: The full boundary of an exported icon – includes
				grid area and icon.
			</li>
			<li class="page-section-list-item" style="margin-left: -20px">
				<img class="guidance-image" src="\assets\images\icon_guidelines_01.png" alt="" />
			</li>
			<li class="page-section-list-item" style="margin-left: -20px">
				Icons also have something called characteristic cues. These are
				the small details that make an icon recognizable as a real-world
				object.
			</li>
			<li class="page-section-list-item" style="margin-left: -20px">
				An example of a characteristic cue would be the keyhole in a
				padlock icon. Without the cue, it could be a purse.
			</li>
			<li class="page-section-list-item" style="margin-left: -20px">
				<img class="guidance-image" src="\assets\images\icon_guidelines_02.png" alt="" />
			</li>
		</ol>
	</section>

	<section class="page-section" id="icon-styles">
		<h2 id="sectionTitleIconStyles" class="page-section-title" title="Copy Link to Clipboard"
			(click)="copyToClipboard('icon-styles')">
			Icon styles <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
		</h2>
		<section class="page-sub-section" id="icon-styles-filled-outlined">
			<h3 id="subSectionTitleIconStylesFilledOutlined" class="page-sub-section-title"
				title="Copy Link to Clipboard" (click)="copyToClipboard('icon-styles-filled-outlined')">
				Filled and outlined <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
			</h3>
			<ul class="page-section-list">
				<li class="page-section-list-item">
					Most of our icons are filled. Filled icons have more
					visibility in dark mode. Filled icons can also be faster to
					recognize, as they look like realistic silhouettes. There
					are other cases where an outline might be faster to
					recognize depending on the characteristic cues.
				</li>
				<li class="page-section-list-item">
					Filled icons can also be used to indicate something is “on”,
					while an outline icon shows it's “off”. An example of a
					filled icon is an eye icon. A filled eye icon demonstrates
					the page is being watched. The outlined icon indicates that
					the page is not being watched.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_03.png" alt="" />
				</li>
			</ul>
		</section>
		<section class="page-sub-section" id="icon-styles-color-contrast">
			<h3 id="subSectionTitleIconStylesColorContrast" class="page-sub-section-title"
				title="Copy Link to Clipboard" (click)="copyToClipboard('icon-styles-color-contrast')">
				Color and contrast <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
			</h3>
			<ul class="page-section-list">
				<li class="page-section-list-item">
					Our icons are available to use as a single color. Dark
					neutrals help with contrast.
				</li>
				<li class="page-section-list-item">
					For components containing confirmations, alerts,
					information, and acknowledgments, follow the severity
					guidelines and color guidelines.
				</li>
				<li class="page-section-list-item">
					Icons should be a solid color and have a contrast ratio of
					4:5:1. Icons shouldn’t be a different color than its
					accompanying text.
					<ul class="page-section-comparison-list">
						<li class="page-section-comparison-list-item comparison-do">
							<div class="page-section-comparison-header">
								<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
									width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
									<g id="Icon/Status/checkmark" stroke="none" stroke-width="1" fill="none"
										fill-rule="evenodd">
										<path
											d="M20.6534688,4.16908529 C20.8789629,3.94363824 21.2445273,3.94363824 21.4700213,4.16908529 L22.8308795,5.52999044 C23.0563735,5.75548448 23.0563735,6.12109588 22.8308795,6.34654293 L8.40527544,20.772194 C8.17973441,20.997641 7.81417,20.997641 7.58872295,20.772194 L1.16912053,14.3525915 C0.94362649,14.1270505 0.94362649,13.7614861 1.16912053,13.5359921 L2.5299787,12.1751339 C2.75551972,11.9496398 3.12108413,11.9496398 3.34657817,12.1751339 L7.99699919,16.8255549 L20.6534688,4.16908529 Z"
											id="" fill="#000000" />
									</g>
								</svg>
								Try This
							</div>
							<div class="page-section-comparison-body">
								Example of error icon and red error message
							</div>
						</li>
						<li class="page-section-comparison-list-item comparison-do-not">
							<div class="page-section-comparison-header">
								<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
									width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
									<title>Icon/NavObjects/close</title>
									<g id="Icon/NavObjects/close" stroke="none" stroke-width="1" fill="none"
										fill-rule="evenodd">
										<path
											d="M21.4999535,10.2499803 C22.3282291,10.2499803 22.9999499,10.9217011 22.9999499,11.7499768 L22.9999499,13.2499732 C22.9999499,14.0782488 22.3282291,14.7499696 21.4999535,14.7499696 L14.7499696,14.7499696 L14.7499696,21.4999535 C14.7499696,22.3282291 14.0782488,22.9999499 13.2499732,22.9999499 L11.7499768,22.9999499 C10.9217011,22.9999499 10.2499803,22.3282291 10.2499803,21.4999535 L10.2499803,14.7499696 L3.5,14.7499696 C2.67172081,14.7499696 2,14.0782488 2,13.2499732 L2,11.7499768 C2,10.9217011 2.67172081,10.2499803 3.5,10.2499803 L10.2499803,10.2499803 L10.2499803,3.5 C10.2499803,2.67172081 10.9217011,2 11.7499768,2 L13.2499732,2 C14.0782488,2 14.7499696,2.67172081 14.7499696,3.5 L14.7499696,10.2499803 L21.4999535,10.2499803 Z"
											id="add2" fill="#000000"
											transform="translate(12.499975, 12.499975) rotate(-315.000000) translate(-12.499975, -12.499975) " />
									</g>
								</svg>
								Not This
							</div>
							<div class="page-section-comparison-body">
								Example of error colored icon and regular text
							</div>
						</li>
					</ul>
				</li>
			</ul>
		</section>
		<section class="page-sub-section" id="icon-styles-scale">
			<h3 id="subSectionTitleIconStylesScale" class="page-sub-section-title" title="Copy Link to Clipboard"
				(click)="copyToClipboard('icon-styles-scale')">
				Scale <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
			</h3>
			<ul class="page-section-list">
				<li class="page-section-list-item">Icons vary in size.</li>
				<li class="page-section-list-item">
					<span class="page-section-list-item-sub-title">Small &#8212;
						16px</span>
					<ul class="page-section-do-list">
						<li class="page-section-list-item">
							Use for data grids, instructional text, and input
							fields
						</li>
						<li class="page-section-list-item">
							Use when space is minimal
						</li>
					</ul>
				</li>
				<li class="page-section-list-item">
					<span class="page-section-list-item-sub-title">Medium
						&#8212; 24px</span>
					<ul class="page-section-do-list">
						<li class="page-section-list-item">
							Use for most icons
						</li>
					</ul>
				</li>
				<li class="page-section-list-item">
					<span class="page-section-list-item-sub-title">Large &#8212;
						36px-48px</span>
					<ul class="page-section-do-list">
						<li class="page-section-list-item">
							Use in navigation to help with wayfinding
						</li>
						<li class="page-section-list-item">Use for emphasis</li>
						<li class="page-section-list-item">
							Use when there’s enough space
						</li>
					</ul>
				</li>
				<li class="page-section-list-item">Icons vary in size.</li>
				<li class="page-section-list-item">
					Do use the same size for the icon as the text. Don’t mix
					sizes.
					<ul class="page-section-comparison-list">
						<li class="page-section-comparison-list-item comparison-do">
							<div class="page-section-comparison-header">
								<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
									width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
									<g id="Icon/Status/checkmark" stroke="none" stroke-width="1" fill="none"
										fill-rule="evenodd">
										<path
											d="M20.6534688,4.16908529 C20.8789629,3.94363824 21.2445273,3.94363824 21.4700213,4.16908529 L22.8308795,5.52999044 C23.0563735,5.75548448 23.0563735,6.12109588 22.8308795,6.34654293 L8.40527544,20.772194 C8.17973441,20.997641 7.81417,20.997641 7.58872295,20.772194 L1.16912053,14.3525915 C0.94362649,14.1270505 0.94362649,13.7614861 1.16912053,13.5359921 L2.5299787,12.1751339 C2.75551972,11.9496398 3.12108413,11.9496398 3.34657817,12.1751339 L7.99699919,16.8255549 L20.6534688,4.16908529 Z"
											id="" fill="#000000" />
									</g>
								</svg>
								Try This
							</div>
							<div class="page-section-comparison-body">
								Example of icon and text the same size
							</div>
						</li>
						<li class="page-section-comparison-list-item comparison-do-not">
							<div class="page-section-comparison-header">
								<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
									width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
									<title>Icon/NavObjects/close</title>
									<g id="Icon/NavObjects/close" stroke="none" stroke-width="1" fill="none"
										fill-rule="evenodd">
										<path
											d="M21.4999535,10.2499803 C22.3282291,10.2499803 22.9999499,10.9217011 22.9999499,11.7499768 L22.9999499,13.2499732 C22.9999499,14.0782488 22.3282291,14.7499696 21.4999535,14.7499696 L14.7499696,14.7499696 L14.7499696,21.4999535 C14.7499696,22.3282291 14.0782488,22.9999499 13.2499732,22.9999499 L11.7499768,22.9999499 C10.9217011,22.9999499 10.2499803,22.3282291 10.2499803,21.4999535 L10.2499803,14.7499696 L3.5,14.7499696 C2.67172081,14.7499696 2,14.0782488 2,13.2499732 L2,11.7499768 C2,10.9217011 2.67172081,10.2499803 3.5,10.2499803 L10.2499803,10.2499803 L10.2499803,3.5 C10.2499803,2.67172081 10.9217011,2 11.7499768,2 L13.2499732,2 C14.0782488,2 14.7499696,2.67172081 14.7499696,3.5 L14.7499696,10.2499803 L21.4999535,10.2499803 Z"
											id="add2" fill="#000000"
											transform="translate(12.499975, 12.499975) rotate(-315.000000) translate(-12.499975, -12.499975) " />
									</g>
								</svg>
								Not This
							</div>
							<div class="page-section-comparison-body">
								Example of mismatch
							</div>
						</li>
					</ul>
				</li>
			</ul>
		</section>
	</section>

	<section class="page-section" id="interactive-icon-states">
		<h2 id="sectionTitleInteractiveIconStates" class="page-section-title" title="Copy Link to Clipboard"
			(click)="copyToClipboard('interactive-icon-states')">
			Interactive icon states <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
		</h2>
		<ul class="page-section-list">
			<li class="page-section-list-item">
				<a href="components/button#variations-icon-only">Interactive icons</a> are icons that users can select.
				Interactive
				icons should include feedback to help users understand what
				actions they can take, if any at all.
			</li>
		</ul>
		<section class="page-sub-section" id="interactive-icon-states-hover">
			<h3 id="sectionTitleInteractiveIconStatesHover" class="page-sub-section-title"
				title="Copy Link to Clipboard" (click)="copyToClipboard('interactive-icon-states-hover')">
				Hover <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
			</h3>
			<ul class="page-section-list">
				<li class="page-section-list-item">
					When a user hovers on an interactive icon, change the icon’s background to a
					circle and change the icon’s color.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_22.png" alt="" />
				</li>
			</ul>
		</section>
		<section class="page-sub-section" id="interactive-icon-states-disabled">
			<h3 id="sectionTitleInteractiveIconStatesDisabled" class="page-sub-section-title"
				title="Copy Link to Clipboard" (click)="copyToClipboard('interactive-icon-states-disabled')">
				Disabled <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
			</h3>
			<ul class="page-section-list">
				<li class="page-section-list-item">
					Interactive icons that can’t be selected by a user should indicate that they’re
					inactive. Use 50% opacity.
				</li>
			</ul>
		</section>
	</section>

	<section class="page-section" id="designing-new-icons">
		<h2 id="sectionTitleDesigningNewIcons" class="page-section-title" title="Copy Link to Clipboard"
			(click)="copyToClipboard('designing-new-icons')">
			Designing new icons <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
		</h2>
		<section class="page-sub-section" id="designing-new-icons-grid">
			<h3 id="sectionTitleDesigningNewIconsGrid" class="page-sub-section-title" title="Copy Link to Clipboard"
				(click)="copyToClipboard('designing-new-icons-grid')">
				Grid <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
			</h3>
			<ul class="page-section-list">
				<li class="page-section-list-item">
					All icons should be drawn on a 24x24px grid frame. Each
					frame includes key shapes and a 2px padding on all sides as
					guidance.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_04.png" alt="" />
				</li>
			</ul>
		</section>
		<section class="page-sub-section" id="designing-new-icons-key-shapes">
			<h3 id="sectionTitleDesigningNewIconsKeyShapes" class="page-sub-section-title"
				title="Copy Link to Clipboard" (click)="copyToClipboard('designing-new-icons-key-shapes')">
				Key shapes <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
			</h3>
			<ul class="page-section-list">
				<li class="page-section-list-item">
					Based on our 24x24px grid, we defined four key shapes:
					circle, square, vertical rectangle, and horizontal
					rectangle. These fundamental shapes help us maintain
					consistency across all system icons.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_05.png" alt="" />
				</li>
				<li class="page-section-list-item">
					For example, these icons share the same fundamental circle
					shape and should be constructed based on that.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_06.png" alt="" />
				</li>
			</ul>
		</section>
		<section class="page-sub-section" id="designing-new-icons-icon-alignment">
			<h3 id="sectionTitleDesigningNewIconsIconAlignment" class="page-sub-section-title"
				title="Copy Link to Clipboard" (click)="copyToClipboard('designing-new-icons-icon-alignment')">
				Icon alignment <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
			</h3>
			<ul class="page-section-list">
				<li class="page-section-list-item">
					All icons should be placed at the center of the grid.
					However, perfectly centered alignment doesn’t always work.
					It’s important to optically align them instead to achieve
					the most balanced placement.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_07.png" alt="" />
				</li>
			</ul>
		</section>
		<section class="page-sub-section" id="designing-new-icons-gap">
			<h3 id="sectionTitleDesigningNewIconsGap" class="page-sub-section-title" title="Copy Link to Clipboard"
				(click)="copyToClipboard('designing-new-icons-gap')">
				Gap <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
			</h3>
			<ul class="page-section-list">
				<li class="page-section-list-item">
					Maintain the same visual gap with a height of 2px.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_08.png" alt="" />
				</li>
			</ul>
		</section>

		<section class="page-sub-section" id="designing-new-icons-off-icon">
			<h3 id="sectionTitleDesigningNewIconsOffIcon" class="page-sub-section-title" title="Copy Link to Clipboard"
				(click)="copyToClipboard('designing-new-icons-off-icon')">
				Off icon <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
			</h3>
			<ul class="page-section-list">
				<li class="page-section-list-item">
					Slashes should go from top left to bottom right, and cutouts
					should follow the stroke size of the system icon.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_12.png" alt="" />
				</li>
			</ul>
		</section>

		<section class="page-sub-section" id="designing-new-icons-modifiers">
			<h3 id="sectionTitleDesigningNewIconsModifiers" class="page-sub-section-title"
				title="Copy Link to Clipboard" (click)="copyToClipboard('designing-new-icons-modifiers')">
				Modifiers <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
			</h3>
			<ul class="page-section-list">
				<li class="page-section-list-item">
					Modifiers are small icons which are drawn on a base icon to
					indicate action or additional information. The most common
					examples are plus icon for add, heart icon for like, follow,
					favorite, etc.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_13.png" alt="" />
				</li>
				<li class="page-section-list-item">
					<span class="page-section-list-item-sub-title">Modifier
						type</span>
					The modifier icon shape should follow the base icon type.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_14.png" alt="" />
				</li>
				<li class="page-section-list-item">
					<span class="page-section-list-item-sub-title">Modifier
						grid</span>
					Filled modifiers should be drawn on a 12x12px grid frame.
					Each frame includes 1px padding on all sides as guidance.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_16.png" alt="" />
				</li>
				<li class="page-section-list-item">
					<span class="page-section-list-item-sub-title">Cutout</span>
					The cutout separates the modifier from the base icon. To
					create the cutout, use a 1.5px outer stroke outline and
					subtract from the base icon.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_17.png" alt="" />
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_18.png" alt="" />
				</li>
				<li class="page-section-list-item">
					<span class="page-section-list-item-sub-title">Placement</span>
					Modifier frame should be overlaid on the bottom right of the
					icon. However, this placement may not always work. If the
					modifiers hide the key details of a certain icon, consider a
					different corner.
				</li>
				<li class="page-section-list-item">
					You can place the modifier on the padding, but it should not
					exceed more than 1px from the base icon live area.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_19.png" alt="" />
				</li>
				<li class="page-section-list-item">
					If there isn’t a right placement for the modifier, we can
					consider creating a new system icon that combines both the
					meanings of the base icon and the modifier.
				</li>
				<li class="page-section-list-item">
					See the icon below as an example. Instead of overlaying the
					modifier on the base icon, placing the plus icon inside the
					base icon would be more reasonable. We don’t need to cut out
					any detail from the base icon and it looks more balanced.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_20.png" alt="" />
				</li>
				<li class="page-section-list-item">
					<span class="page-section-list-item-sub-title">Base icon
						adjustment</span>
					A few adjustments can be made to simplify the overall look
					without affecting the original meaning of the base icon.
				</li>
				<li class="page-section-list-item">
					<img class="guidance-image" src="\assets\images\icon_guidelines_21.png" alt="" />
				</li>
			</ul>
		</section>
		<section class="page-sub-section" id="designing-new-icons-naming-new-icons">
			<h3 id="sectionTitleDesigningNewIconsNamingNewIcons" class="page-sub-section-title"
				title="Copy Link to Clipboard" (click)="
					copyToClipboard('designing-new-icons-naming-new-icons')
				">
				Naming new icons <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
			</h3>
			<ul class="page-section-list">
				<li class="page-section-list-item">
					New icon names should be intuitive and simple. The object
					name should be before the modifier or direction. So, it’s
					table-clear, not clear-table and chevron-right, not
					right-chevron.
				</li>
			</ul>
		</section>
	</section>
	<section class="page-section" id="resources">
		<h2 id="sectionTitleResources" class="page-section-title" title="Copy Link to Clipboard"
			(click)="copyToClipboard('resources')">
			Resources <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
		</h2>

		<ul class="page-section-list">
			<li class="page-section-list-item resource-list-item">
				<a href="https://www.krishaweb.com/blog/outline-icons-vs-solid-icons-which-are-faster-to-recognize-and-when/"
					target="_blank">Outline Icons vs. Solid Icons: Which are Faster to
					Recognize and When?</a><span class="resource-source">KrishaWeb</span>
			</li>
			<li class="page-section-list-item resource-list-item">
				<a href="https://digitalcommunications.wp.st-andrews.ac.uk/2021/09/23/should-icons-be-solid-or-hollow/"
					target="_blank">Should icons be solid or hollow?</a><span class="resource-source">University of St
					Andrews</span>
			</li>
			<li class="page-section-list-item resource-list-item">
				<a href="https://cdr.lib.unc.edu/concern/masters_papers/6w924g35w" target="_blank">Filled-in vs. Outline
					Icons: The Impact of Icon Style on
					Usability</a><span class="resource-source">University of
					Carolina</span>
			</li>
		</ul>
	</section>
	<section class="page-section" id="related">
		<h2 id="sectionTitleRelated" class="page-section-title" title="Copy Link to Clipboard"
			(click)="copyToClipboard('related')">
			Related <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
		</h2>
		<span class="page-related-list-title">Referenced in this section:</span>
		<ul class="page-section-list page-related-list">
			<li class="page-related-list-item">
				<a href="components/button">Button component</a>
			</li>
			<li class="page-related-list-item">
                <a href="guidelines/colors">Colors guidelines</a>
            </li>
			<li class="page-related-list-item">
				<a href="guidelines/content">Content guidelines</a>
			</li>
			<li class="page-related-list-item">
				<a href="components/icon">Icon component</a>
			</li>
		</ul>
	</section>
	<section class="page-section" id="history">
		<h2 id="sectionTitleHistory" class="page-section-title" title="Copy Link to Clipboard"
			(click)="copyToClipboard('history')">
			Revision history <kendo-svg-icon [icon]="linkIcon"></kendo-svg-icon>
		</h2>
		<ul class="page-history-list">
			<li class="page-history-list-item" *ngFor="
					let subItem of changelogService.getChangelogBySection(
						'Icon Guidelines'
					)
				">
				<span class="page-history-list-item-title">{{
					changelogService.formatDate(subItem.changeDate)
					}}</span>
				<a class="page-history-list-item-link" target="_blank" *ngIf="subItem.workId != ''" [attr.href]="
						'https://beyondtrust.atlassian.net/jira/software/c/projects/SDS/issues/' +
						subItem.workId
					">[{{ subItem.workId }}]</a>{{ subItem.changeName }}
			</li>
		</ul>
	</section>
</main>